// Considered: AuditEntryMolecule/MarketplaceActivityRowMolecule (plan OQ-4 ratified target) —
// rejected on inspection: both are typed against real DB-backed domain shapes (`AuditEntry`'s
// grant/revoke relations, `MarketplaceActivity`'s closed `ActivityAction` enum scoped to
// publisher/channel entities). A composer action-log row is neither — forcing it through either
// type would mean inventing enum values the type doesn't declare. This molecule follows the same
// icon-row VISUAL pattern (timeline dot + content) with its own honestly-typed prop.
import React from 'react';
import { useTranslation } from 'localization';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import type { ComposerActionLogEntry } from '../../../composer/actionLog';
import './ActionLogMolecule.scss';

export interface ActionLogMoleculeProps {
  entries: ComposerActionLogEntry[];
  className?: string;
}

const KIND_ICON: Record<ComposerActionLogEntry['kind'], string> = {
  fired: 'pi-bolt',
  delivered: 'pi-arrow-right',
  blocked: 'pi-ban',
};

/**
 * Try mode's sandboxed action log (SC5): every fired action, every wire delivery it produced, and
 * every write-shaped action shown as blocked. Newest first — Try mode is an exploratory session,
 * not an audit trail, so the most recent interaction is what the author is checking.
 */
const ActionLogMolecule: React.FC<ActionLogMoleculeProps> = ({ entries, className = '' }) => {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return (
      <div
        className={['action-log', 'action-log--empty', className].filter(Boolean).join(' ')}
        data-testid="composer-action-log"
      >
        <EmptyStateAtom icon="pi-bolt" title={t('composer.tryMode.actionLog.emptyTitle')} />
      </div>
    );
  }

  const ordered = [...entries].reverse();

  return (
    <ul className={['action-log', className].filter(Boolean).join(' ')} data-testid="composer-action-log">
      {ordered.map((entry) => (
        <li
          key={entry.id}
          className={`action-log__row action-log__row--${entry.kind}`}
          data-testid={`action-log-entry-${entry.kind}`}
        >
          <i className={`pi ${KIND_ICON[entry.kind]} action-log__icon`} aria-hidden="true" />
          <div className="action-log__body">
            <span className="action-log__summary">
              {t(`composer.tryMode.actionLog.kind.${entry.kind}`, {
                action: entry.actionName,
                component: entry.sourceComponentId,
                target: entry.targetComponentId,
              })}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default ActionLogMolecule;
